import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Menu } from '@mui/material';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';

import {
    Table,
    TableBodyContainer,
    TableCell,
    TableHead,
    TableHeadRow,
    TableRow,
} from '../../../components/Table';
import Pagination from '../../../components/Pagination';
import TableErrorComponent from '../../../components/Error/TableError';
import useGetRequest from '../../../hooks/getRequest.query';
import { getPatientConditionTestReports } from '../../../services/patient/PatientReports.hook';
import {
    getErrorMessage,
    isErrorMsgNotReadable,
} from '../../../common/utils/genericErrors';
import CustomTooltip from '../../../components/CustomTooltip';
import FilterTile from '../../../components/filter-tile';
import Modal from '../../../components/Modal';
import AssetViewer from '../features/health-conditions/components/AssetViewer';
import { isPDF } from '../../../common/utils/fileUtils';

// Server-driven list; no mock data

const PatientReportListTable = ({
    filterData,
    pageLimit,
    currentPageNumber,
    handlePageNumber,
    setFilterData,
    setPageLimit,
    setPageNumber,
}: any) => {
    const [showFilter, setShowFilter] = useState(false);
    const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(
        null
    );
    const [showDiseaseDropdown, setShowDiseaseDropdown] = useState(false);
    const diseaseDropdownRef = useRef<HTMLDivElement>(null);
    const resetFormRef = useRef<any>(null);
    // Defer network calls until SUBMIT
    const [pendingFilter, setPendingFilter] = useState<any>(null);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [selectedReport, setSelectedReport] = useState<any>(null);

    const [searchTerm, setSearchTerm] = useState('');

    const searchSchema = Yup.object().shape({
        searchName: Yup.string().trim().max(100, 'Too long').nullable(),
    });

    const onSearchSubmit = (
        values: { facilityCode?: string; searchName?: string },
        actions: any
    ) => {
        setSearchTerm((values.searchName || '').trim());
        setPageNumber(1);
        actions.setSubmitting(false);
    };

    const handleClearSearch = (resetForm: any) => {
        setSearchTerm('');
        setPageNumber(1);
        resetForm();
    };

    const diseaseTypes = [
        'Refractive Errors (Vision Problems)',
        'Cataract',
        'Glaucoma',
        'Dry Eye Syndrome',
        'Diabetic Retinopathy',
        'Eye Allergies',
    ];

    const handleClearAllFilters = (resetForm: any) => {
        setSearchTerm('');
        setFilterData({
            diseaseReportTypes: [...diseaseTypes],
            fromDate: '',
            toDate: '',
        });
        setPageNumber(1);
        if (resetForm) {
            resetForm();
        }
    };

    // Fetch reports
    const selectedConditions = filterData.diseaseReportTypes || [];
    const { data, isLoading, isError, error, refetch }: any = useGetRequest(
        [
            'getPatientConditionTestReports',
            currentPageNumber,
            pageLimit,
            searchTerm,
            // include all selected conditions in the key for cache correctness
            (selectedConditions || []).join('|'),
            filterData?.fromDate,
            filterData?.toDate,
        ],
        async () =>
            await getPatientConditionTestReports({
                search: searchTerm || undefined,
                conditionNames:
                    (selectedConditions || []).length > 0
                        ? selectedConditions
                        : undefined,
                startDate: filterData?.fromDate || undefined,
                endDate: filterData?.toDate || undefined,
                limit: pageLimit,
                page: currentPageNumber,
            }),
        {
            refetchOnWindowFocus: false,
            keepPreviousData: true,
        }
    );

    useEffect(() => {
        refetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const reportData = data?.data?.data?.reports || [];
    const totalReportsCount = data?.data?.results || 0;
    const totalPages = Math.ceil((totalReportsCount || 0) / pageLimit);

    const errorMsg = isError
        ? isErrorMsgNotReadable(error?.response?.data?.message)
            ? getErrorMessage(error?.response?.status)
            : 'No data available'
        : '';

    const handleFilterChange = (newFilterData: any) => {
        setFilterData(newFilterData);
        setPageNumber(1);
    };

    const handlePageChange = (page: number) => {
        handlePageNumber(page);
    };

    const handlePageLimitChange = (limit: string | number) => {
        setPageLimit(Number(limit));
        setPageNumber(1);
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                diseaseDropdownRef.current &&
                !diseaseDropdownRef.current.contains(event.target as Node)
            ) {
                setShowDiseaseDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Ensure all disease types are selected by default
    useEffect(() => {
        const current = filterData.diseaseReportTypes;
        if (!Array.isArray(current) || current.length === 0) {
            handleFilterChange({
                ...filterData,
                diseaseReportTypes: [...diseaseTypes],
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleDiseaseTypeToggle = (type: string) => {
        const base = pendingFilter ?? filterData;
        const currentTypes = base.diseaseReportTypes || [];
        const newTypes = currentTypes.includes(type)
            ? currentTypes.filter((t: string) => t !== type)
            : [...currentTypes, type];

        setPendingFilter({
            ...base,
            diseaseReportTypes: newTypes,
        });
    };

    const getDisplayText = () => {
        const active = pendingFilter ?? filterData;
        const selected = active?.diseaseReportTypes || diseaseTypes;
        if (selected.length === 0) return 'All';
        if (selected.length === 1) return selected[0];
        return `${selected.length} selected`;
    };

    const handleViewReport = (report: any) => {
        setSelectedReport(report);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedReport(null);
    };

    // Server paginated; no client filtering/pagination

    return (
        <div className="card w-100">
            <Modal
                open={isModalOpen}
                overlayStyles={{
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                }}
            >
                <AssetViewer
                    styles={{
                        backgroundColor: 'white',
                    }}
                    onClose={handleCloseModal}
                    userData={selectedReport}
                />
            </Modal>
            <div className="card-header border-0 w-100">
                <div className="d-flex justify-content-between align-items-center w-100">
                    <div className="d-flex align-items-center">
                        <h1 className="fs-1 text-dark fw-bolder text-uppercase">
                            PATIENT REPORTS
                        </h1>
                        <button
                            type="button"
                            className="btn btn-sm btn-icon btn-active-color-primary ms-3"
                            onClick={(e) => {
                                e.preventDefault();
                                refetch();
                            }}
                            disabled={isLoading}
                            title="Refresh data"
                            style={{
                                border: 'none',
                                background: 'transparent',
                                padding: '4px',
                            }}
                        >
                            <i
                                className={`fas fa-redo fs-3 ${
                                    isLoading ? 'fa-spin' : ''
                                }`}
                                style={{
                                    opacity: isLoading ? 0.6 : 1,
                                }}
                            ></i>
                        </button>
                    </div>
                    <div className="d-flex align-items-center">
                        {/* Search Bar */}
                        <div className="py-2 me-20">
                            <Formik
                                initialValues={{
                                    facilityCode: '',
                                    searchName: '',
                                }}
                                validationSchema={searchSchema}
                                onSubmit={onSearchSubmit}
                            >
                                {(formProps: any) => {
                                    resetFormRef.current = formProps.resetForm;
                                    return (
                                        <Form className="w-100 d-flex flex-column align-items-center justify-content-center">
                                            <div className="d-flex ">
                                                <Field
                                                    name="searchName"
                                                    placeholder="Search Patient"
                                                    className="form-control form-control-lg form-control-solid"
                                                />
                                                <button
                                                    disabled={
                                                        formProps.isSubmitting ||
                                                        !(
                                                            formProps.isValid &&
                                                            formProps.dirty
                                                        )
                                                    }
                                                    type="submit"
                                                    className="ms-3 btn btn-lg btn-primary"
                                                >
                                                    Search
                                                </button>
                                                {searchTerm && (
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            handleClearSearch(
                                                                formProps.resetForm
                                                            )
                                                        }
                                                        className="ms-2 btn btn-lg btn-light"
                                                        title="Clear search"
                                                    >
                                                        <i className="fas fa-times"></i>
                                                    </button>
                                                )}
                                            </div>
                                            <div className="w-100 d-flex flex-column align-items-center text-danger mt-1">
                                                <ErrorMessage name="searchName" />
                                            </div>
                                        </Form>
                                    );
                                }}
                            </Formik>
                        </div>

                        <button
                            className="btn btn-sm btn-primary"
                            onClick={(e) => {
                                setFilterAnchorEl(e.currentTarget);
                                setPendingFilter({ ...filterData });
                                setShowFilter(!showFilter);
                            }}
                        >
                            <i className="fas fa-filter"></i>
                            FILTER
                        </button>
                    </div>
                </div>
            </div>
            <FilterTile
                filterData={{
                    Name: searchTerm || '',
                    'From date': filterData?.fromDate || '',
                    'To date': filterData?.toDate || '',
                }}
                keyNames={{
                    Name: 'Name',
                    'From date': 'From date',
                    'To date': 'To date',
                }}
                clearAll={() => {
                    handleClearAllFilters(resetFormRef.current);
                }}
            />
            {showFilter && (
                <div>
                    <Menu
                        id="basic-menu"
                        anchorEl={filterAnchorEl}
                        open={showFilter}
                        onClose={() => {
                            setShowFilter(false);
                            setFilterAnchorEl(null);
                        }}
                        MenuListProps={{
                            'aria-labelledby': 'basic-button',
                        }}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                    >
                        <div className="py-5 px-8">
                            <div className="w-250px mt-5 mb-5">
                                <label className="form-label fs-4 fw-bolder">
                                    DISEASE/REPORT TYPE
                                </label>
                                <div
                                    className="position-relative"
                                    ref={diseaseDropdownRef}
                                >
                                    <div
                                        className="form-control form-control-lg cursor-pointer position-relative"
                                        onClick={() =>
                                            setShowDiseaseDropdown(
                                                !showDiseaseDropdown
                                            )
                                        }
                                        style={{ cursor: 'pointer' }}
                                    >
                                        {getDisplayText()}
                                        <i
                                            className={`fas position-absolute end-0 me-3 ${
                                                showDiseaseDropdown
                                                    ? 'fa-chevron-up'
                                                    : 'fa-chevron-down'
                                            }`}
                                            style={{
                                                top: '50%',
                                                transform: 'translateY(-50%)',
                                                transition: 'all 0.3s',
                                            }}
                                        ></i>
                                    </div>

                                    {showDiseaseDropdown && (
                                        <div
                                            className="position-absolute w-100 bg-white border border-gray-300 rounded"
                                            style={{
                                                top: '100%',
                                                left: 0,
                                                zIndex: 1000,
                                                maxHeight: '200px',
                                                overflowY: 'auto',
                                            }}
                                        >
                                            {diseaseTypes.map((type) => (
                                                <div
                                                    key={type}
                                                    className="px-3 py-2 cursor-pointer d-flex align-items-center"
                                                    onClick={() =>
                                                        handleDiseaseTypeToggle(
                                                            type
                                                        )
                                                    }
                                                    style={{
                                                        backgroundColor: (
                                                            (
                                                                pendingFilter ??
                                                                filterData
                                                            )
                                                                ?.diseaseReportTypes ||
                                                            diseaseTypes
                                                        ).includes(type)
                                                            ? '#e3f2fd'
                                                            : 'transparent',
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        if (
                                                            !(
                                                                (
                                                                    pendingFilter ??
                                                                    filterData
                                                                )
                                                                    ?.diseaseReportTypes ||
                                                                diseaseTypes
                                                            ).includes(type)
                                                        ) {
                                                            e.currentTarget.style.backgroundColor =
                                                                '#f5f5f5';
                                                        }
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        if (
                                                            !(
                                                                (
                                                                    pendingFilter ??
                                                                    filterData
                                                                )
                                                                    ?.diseaseReportTypes ||
                                                                diseaseTypes
                                                            ).includes(type)
                                                        ) {
                                                            e.currentTarget.style.backgroundColor =
                                                                'transparent';
                                                        }
                                                    }}
                                                >
                                                    <div className="form-check mb-0 me-2">
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            checked={(
                                                                (
                                                                    pendingFilter ??
                                                                    filterData
                                                                )
                                                                    ?.diseaseReportTypes ||
                                                                diseaseTypes
                                                            ).includes(type)}
                                                            onChange={() => {}} // Handled by parent onClick
                                                        />
                                                    </div>
                                                    <span className="text-gray-900 fs-6">
                                                        {type}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="w-250px mt-5 mb-5">
                                <label className="form-label fs-4 fw-bolder required">
                                    FROM DATE
                                </label>
                                <input
                                    id="fromDate"
                                    type="date"
                                    max={new Date().toISOString().split('T')[0]}
                                    value={
                                        (pendingFilter ?? filterData)?.fromDate
                                    }
                                    onChange={(e) =>
                                        setPendingFilter({
                                            ...((pendingFilter ?? filterData) ||
                                                {}),
                                            fromDate: e.target.value,
                                        })
                                    }
                                    className="form-control cursor-pointer"
                                />
                            </div>
                            <div className="w-250px mt-5">
                                <label className="form-label fs-4 fw-bolder required">
                                    TO DATE
                                </label>
                                <input
                                    id="toDate"
                                    type="date"
                                    max={new Date().toISOString().split('T')[0]}
                                    value={
                                        (pendingFilter ?? filterData)?.toDate
                                    }
                                    onChange={(e) =>
                                        setPendingFilter({
                                            ...((pendingFilter ?? filterData) ||
                                                {}),
                                            toDate: e.target.value,
                                        })
                                    }
                                    className="form-control"
                                />
                            </div>
                        </div>

                        <div className="my-5 d-flex justify-content-center align-items-center">
                            <button
                                type="button"
                                onClick={() => {
                                    if (pendingFilter) {
                                        handleFilterChange(pendingFilter);
                                    }
                                    setShowFilter(false);
                                    setFilterAnchorEl(null);
                                }}
                                className="w-250px btn btn-sm btn-primary"
                            >
                                SUBMIT
                            </button>
                        </div>
                    </Menu>
                </div>
            )}

            {isError ? (
                <TableErrorComponent errorMessage={errorMsg} />
            ) : reportData.length === 0 ? (
                <>
                    <div
                        className="card-body d-flex justify-content-center align-items-center"
                        style={{ height: '400px' }}
                    >
                        <h2 className="text-danger">No Patient Report</h2>
                    </div>
                    {!isError && (
                        <Pagination
                            prevDisabled={currentPageNumber === 1}
                            nextDisabled={currentPageNumber === totalPages}
                            handlePrev={() =>
                                handlePageChange(currentPageNumber - 1)
                            }
                            handleNext={() =>
                                handlePageChange(currentPageNumber + 1)
                            }
                            handleCallback={handlePageLimitChange}
                            dataCount={totalReportsCount}
                            pageLimit={pageLimit}
                            pageNumber={currentPageNumber}
                        />
                    )}
                </>
            ) : (
                <div className="card-body py-0">
                    <style>
                        {`
                            .patient-report-table tbody tr td {
                                padding: 16px 8px !important;
                            }
                        `}
                    </style>
                    <div className="card-body p-0 patient-report-table">
                        <Table>
                            <TableHeadRow>
                                <TableHead width={50} align="center">
                                    SL NO
                                </TableHead>
                                <TableHead width={150}>PATIENT NAME</TableHead>
                                <TableHead width={120}>UHID</TableHead>
                                <TableHead width={180}>
                                    CONDITION / REPORT TYPE
                                </TableHead>
                                <TableHead width={200}>SYMPTOMS</TableHead>
                                <TableHead width={120}>DATE</TableHead>
                                <TableHead width={120}>REPORT FILE</TableHead>
                            </TableHeadRow>

                            <TableBodyContainer
                                isLoading={isLoading}
                                isError={isError}
                                errorMessage={errorMsg}
                            >
                                {reportData.map(
                                    (report: any, index: number) => (
                                        <TableRow
                                            key={`${report.uhid}-${report.testDate}-${index}`}
                                            collapsible={false}
                                        >
                                            <TableCell align="center">
                                                {(currentPageNumber - 1) *
                                                    pageLimit +
                                                    index +
                                                    1}
                                            </TableCell>
                                            <TableCell>
                                                <div className="d-flex align-items-center">
                                                    <div className="d-flex justify-content-start flex-column">
                                                        <Link
                                                            to={`/patient/overview?pId=${report.uhid}`}
                                                        >
                                                            <span className="text-gray-900 fw-bold text-hover-primary fs-6">
                                                                {
                                                                    report.patientName
                                                                }
                                                            </span>
                                                        </Link>
                                                    </div>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <span className="text-gray-600 fw-bold fs-6">
                                                    {report.uhid}
                                                </span>
                                            </TableCell>
                                            <TableCell>
                                                <span className="text-gray-900 fw-bold fs-6">
                                                    {report.conditionName}
                                                </span>
                                            </TableCell>
                                            <TableCell>
                                                <CustomTooltip
                                                    title={
                                                        report.symptoms || 'N/A'
                                                    }
                                                    placement="top"
                                                >
                                                    <span
                                                        className="text-gray-600 fw-bold fs-6"
                                                        style={{
                                                            display: 'block',
                                                            overflow: 'hidden',
                                                            textOverflow:
                                                                'ellipsis',
                                                            whiteSpace:
                                                                'nowrap',
                                                            maxWidth: '200px',
                                                        }}
                                                    >
                                                        {report.symptoms ||
                                                            'N/A'}
                                                    </span>
                                                </CustomTooltip>
                                            </TableCell>
                                            <TableCell>
                                                <span className="text-gray-600 fw-bold fs-6">
                                                    {report.testDate || 'N/A'}
                                                </span>
                                            </TableCell>
                                            <TableCell>
                                                <div className="d-flex justify-content-center">
                                                    {report.testFile ? (
                                                        isPDF(
                                                            report.testFile
                                                        ) ? (
                                                            <a
                                                                href={
                                                                    report.testFile
                                                                }
                                                                download
                                                                className="btn btn-sm btn-icon btn-active-color-primary"
                                                                title="Download PDF"
                                                            >
                                                                <i className="fas fa-download fs-3"></i>
                                                            </a>
                                                        ) : (
                                                            <button
                                                                onClick={() =>
                                                                    handleViewReport(
                                                                        report
                                                                    )
                                                                }
                                                                className="btn btn-bg-light btn-color-muted btn-active-color-primary btn-sm px-4"
                                                            >
                                                                View
                                                            </button>
                                                        )
                                                    ) : (
                                                        <span className="text-gray-500">
                                                            N/A
                                                        </span>
                                                    )}
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    )
                                )}
                            </TableBodyContainer>
                        </Table>

                        {!isError && (
                            <Pagination
                                prevDisabled={currentPageNumber === 1}
                                nextDisabled={currentPageNumber === totalPages}
                                handlePrev={() =>
                                    handlePageChange(currentPageNumber - 1)
                                }
                                handleNext={() =>
                                    handlePageChange(currentPageNumber + 1)
                                }
                                handleCallback={handlePageLimitChange}
                                dataCount={totalReportsCount}
                                pageLimit={pageLimit}
                                pageNumber={currentPageNumber}
                            />
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default PatientReportListTable;
